import React, { useEffect, useRef, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { reconstructFile } from '../components/admin/ChunkedFileUpload';

export default function ProjectViewer3D() {
  const urlParams = new URLSearchParams(window.location.search);
  const projectId = urlParams.get('id');
  const containerRef = useRef(null);
  const speedRef = useRef(0.1);
  const [error, setError] = useState(null);
  const [speed, setSpeed] = useState(0.1);
  const [isLoading3D, setIsLoading3D] = useState(true);

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const projects = await base44.entities.Project.filter({ id: projectId });
      return projects[0];
    },
    enabled: !!projectId
  });

  useEffect(() => {
    if (!project?.glb_file_data || !containerRef.current) return;

    let scene, camera, renderer, model;
    let isPointerLocked = false;
    let yaw = 0, pitch = 0;
    const keys = {};

    // Setup scene
    scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);

    // Setup camera
    camera = new THREE.PerspectiveCamera(
      75,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(0, 1.6, 5);

    // Setup renderer
    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 10, 7.5);
    scene.add(directionalLight);

    // Load GLB
    const loader = new GLTFLoader();

    // Check if GLB is chunked or standard
    const loadGLB = async () => {
      try {
        let glbUrl;
        const glbData = project.glb_file_data;

        if (glbData && glbData.chunked && glbData.manifestUrl && glbData.partUrls) {
          console.log('Loading chunked GLB...');
          // Reconstruct chunked file
          const manifestResponse = await fetch(glbData.manifestUrl);
          const manifest = await manifestResponse.json();
          console.log('Manifest loaded:', manifest);
          const blob = await reconstructFile(manifest, glbData.partUrls);
          console.log('Blob reconstructed:', blob.size, 'bytes');
          glbUrl = URL.createObjectURL(blob);
        } else if (glbData && glbData.url) {
          console.log('Loading standard GLB from URL:', glbData.url);
          // Standard file
          glbUrl = glbData.url;
        } else {
          console.error('No GLB data available:', glbData);
          setError('Nessun file GLB disponibile');
          setIsLoading3D(false);
          return;
        }

        loader.load(
          glbUrl,
          (gltf) => {
            model = gltf.scene;
            scene.add(model);
            setIsLoading3D(false);

            // Cleanup blob URL if created
            if (glbData && glbData.chunked) {
              URL.revokeObjectURL(glbUrl);
            }
          },
          undefined,
          (err) => {
            console.error('Error loading GLB:', err);
            setError('Errore nel caricamento del modello 3D');
            setIsLoading3D(false);
          }
        );
      } catch (err) {
        console.error('Error reconstructing GLB:', err);
        setError('Errore nella ricostruzione del modello 3D');
        setIsLoading3D(false);
      }
    };

    loadGLB();

    // Key events
    const onKeyDown = (e) => { 
      // ESC to exit
      if (e.key === 'Escape') {
        window.location.href = createPageUrl('ProjectDetail') + '?id=' + projectId;
        return;
      }
      if (e.key === 'Shift') {
        keys['shift'] = true;
      } else {
        keys[e.key.toLowerCase()] = true; 
      }
    };
    const onKeyUp = (e) => { 
      if (e.key === 'Shift') {
        keys['shift'] = false;
      } else {
        keys[e.key.toLowerCase()] = false; 
      }
    };

    // Mouse events
    const onMouseMove = (e) => {
      if (!isPointerLocked) return;
      yaw -= e.movementX * 0.002;
      pitch -= e.movementY * 0.002;
      pitch = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, pitch));
    };

    const onClick = () => {
      if (renderer?.domElement) {
        renderer.domElement.requestPointerLock();
      }
    };

    const onPointerLockChange = () => {
      isPointerLocked = renderer?.domElement && document.pointerLockElement === renderer.domElement;
    };

    const onWheel = (e) => {
      e.preventDefault();
      const newSpeed = Math.max(0.001, Math.min(0.5, speedRef.current + e.deltaY * -0.0001));
      speedRef.current = newSpeed;
      setSpeed(newSpeed);
    };

    // Event listeners
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('pointerlockchange', onPointerLockChange);
    if (renderer?.domElement) {
      renderer.domElement.addEventListener('click', onClick);
      renderer.domElement.addEventListener('wheel', onWheel, { passive: false });
    }

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);

      // Update camera rotation
      camera.rotation.order = 'YXZ';
      camera.rotation.y = yaw;
      camera.rotation.x = pitch;

      // Movement
      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);

      if (keys['w'] || keys['arrowup']) camera.position.add(forward.multiplyScalar(speedRef.current));
      if (keys['s'] || keys['arrowdown']) camera.position.add(forward.multiplyScalar(-speedRef.current));
      if (keys['a'] || keys['arrowleft']) camera.position.add(right.multiplyScalar(-speedRef.current));
      if (keys['d'] || keys['arrowright']) camera.position.add(right.multiplyScalar(speedRef.current));
      if (keys[' ']) camera.position.y += speedRef.current; // Space = up
      if (keys['shift']) camera.position.y -= speedRef.current; // Shift = down

      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current) return;
      camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      if (renderer?.domElement) {
        renderer.domElement.removeEventListener('click', onClick);
        renderer.domElement.removeEventListener('wheel', onWheel);
      }
      window.removeEventListener('resize', handleResize);
      if (renderer) {
        renderer.dispose();
      }
      if (containerRef.current && renderer?.domElement && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, [project]);

  if (!project) {
    return (
      <div className="pt-32 flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#9c8b7a] mb-4" />
        <p className="text-[#7a7a7a]">Caricamento progetto...</p>
      </div>
    );
  }

  if (!project.glb_file_data) {
    return (
      <div className="pt-32 flex flex-col items-center justify-center min-h-screen">
        <AlertCircle className="w-12 h-12 text-[#9c8b7a] mb-4" />
        <p className="text-[#7a7a7a] mb-4">Modello 3D non disponibile per questo progetto</p>
        <Link to={createPageUrl('ProjectDetail') + '?id=' + projectId} className="text-[#9c8b7a] hover:underline">
          Torna al progetto
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-32 flex flex-col items-center justify-center min-h-screen">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-[#7a7a7a] mb-4">{error}</p>
        <Link to={createPageUrl('ProjectDetail') + '?id=' + projectId} className="text-[#9c8b7a] hover:underline">
          Torna al progetto
        </Link>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/70 to-transparent p-6">
        <Link
          to={createPageUrl('ProjectDetail') + '?id=' + projectId}
          className="inline-flex items-center gap-2 text-white hover:text-white/70 transition-colors text-sm"
        >
          <ArrowLeft size={16} />
          Torna al progetto
        </Link>
        <h1 className="text-white text-2xl font-serif mt-4">{project.title}</h1>
      </div>

      {/* Controls info */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/70 to-transparent p-6">
        <div className="text-white text-sm space-y-1">
          <p>🖱️ Clicca per attivare i controlli</p>
          <p>⌨️ WASD/Frecce per muoverti • Spazio/Shift per salire/scendere • Mouse per guardare</p>
          <p>⌨️ Rotella per velocità • ESC per uscire</p>
          <p className="text-white/70">Velocità attuale: {speed.toFixed(3)}</p>
        </div>
      </div>

      {/* Loading overlay */}
      {isLoading3D && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
            <p className="text-white">Caricamento modello 3D...</p>
          </div>
        </div>
      )}

      {/* 3D Canvas */}
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
}